import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import { id as makeId, tx } from '@instantdb/react';
import { Pressable, Text, View } from 'react-native';

import { db } from '@/lib/db/instant';
import { queryServer, transactServer, transactCreateServer, matchesSavedFields } from '@/lib/db/server';
import { useServerQuery } from '@/lib/db/useServerQuery';
import { findPreset, type CompoundPreset } from '@/lib/pk/presets';
import type { Schedule } from '@/lib/pk/recommend';
import { scheduleFromRecord, reviseSchedule, serializeVersion } from '@/lib/pk/schedule';
import { positive, validDate, validCadence } from '@/lib/pk/validation';
import { prepareDose } from './dose-record';

// Domain types kept identical to the prior mock surface so screens don't change.
export type DoseStatus = 'confirmed' | 'pending' | 'skipped';

export type LoggedDose = {
  id: string;
  takenAt: Date;
  amount: number;
  status: DoseStatus;
};

export type UserCompound = {
  id: string;
  preset: CompoundPreset;
  schedule: Schedule;
  concentrationMgPerMl: number;
  doses: LoggedDose[];
};

type ActiveView = {
  uc: UserCompound;
  confirmedDoses: LoggedDose[];
  pendingDoses: LoggedDose[];
  skippedDoses: LoggedDose[];
};

type Ctx = {
  userCompounds: UserCompound[];
  loading: boolean;
  activeId: string;
  setActiveId: (id: string) => void;
  active: UserCompound | null;
  view: ActiveView | null;

  setStatus: (doseId: string, status: DoseStatus) => Promise<void>;
  deleteDose: (doseId: string) => Promise<void>;
  updateDose: (
    doseId: string,
    patch: Partial<Pick<LoggedDose, 'amount' | 'takenAt' | 'status'>>,
  ) => Promise<void>;
  takeNow: (amountMg: number) => Promise<void>;
  confirmNew: (amountMg: number, takenAt: Date) => Promise<void>;
  createDose: (input: { amount: number; takenAt: Date; status: DoseStatus }) => Promise<void>;

  updateSchedule: (patch: Partial<Pick<Schedule, 'defaultDoseMg' | 'cadenceHours' | 'startsAt'>>) => Promise<void>;
  updateCompoundSettings: (patch: Partial<Pick<Schedule, 'defaultDoseMg' | 'cadenceHours' | 'startsAt'>>, concentrationMgPerMl: number) => Promise<void>;
  updateConcentration: (concentrationMgPerMl: number) => Promise<void>;

  createCompound: (input: {
    compoundId: string;
    concentrationMgPerMl: number;
    defaultDoseMg: number;
    cadenceHours: number;
    scheduleStartsAt: Date;
    seedFirstDose?: boolean;
  }) => Promise<string>;
  deleteCompound: (compoundId: string) => Promise<void>;

  logout: () => void;
};

const CompoundsContext = createContext<Ctx | null>(null);

type RemoteUserCompound = {
  id: string;
  compoundId: string;
  shortCode: string;
  concentrationMgPerMl: number;
  defaultDoseMg: number;
  cadenceHours: number;
  scheduleStartsAt: string | Date;
  currentScheduleEffectiveAt?: string;
  scheduleHistory?: unknown;
  isActive: boolean;
  createdAt: string | Date;
  doses?: RemoteDose[];
};

type RemoteDose = {
  id: string;
  amount: number;
  takenAt: string | Date;
  status: string;
  createdAt: string | Date;
};

function toDate(v: string | Date): Date {
  return v instanceof Date ? v : new Date(v);
}

function mapRemote(uc: RemoteUserCompound): UserCompound {
  const preset = findPreset(uc.compoundId);
  if (!preset) throw new Error(`Unknown compound: ${uc.compoundId}`);
  positive(uc.concentrationMgPerMl, 'Concentration');
  return {
    id: uc.id,
    preset,
    schedule: scheduleFromRecord(uc),
    concentrationMgPerMl: uc.concentrationMgPerMl,
    doses: (uc.doses ?? []).map((dose) => {
      return { id: dose.id, ...prepareDose({ ...dose, takenAt: toDate(dose.takenAt) }) };
    }),
  };
}

export function CompoundsProvider({ children }: PropsWithChildren) {
  const { user } = db.useAuth();

  const { isLoading, data, error, refresh, isStale, fetchedAt } = useServerQuery(
    user
      ? {
          userCompounds: {
            $: { where: { 'owner.id': user.id } },
            doses: {},
          },
        }
      : null,
  );

  const mappedData = useMemo(() => {
    try {
      const compounds = ((data?.userCompounds ?? []) as unknown as RemoteUserCompound[])
        .map(mapRemote)
        .sort((a, b) => a.id.localeCompare(b.id));
      return { compounds, error: null };
    } catch (cause) {
      return { compounds: [] as UserCompound[], error: cause instanceof Error ? cause.message : 'Invalid saved compound data' };
    }
  }, [data]);
  const doseIntents = useRef(new Map<string, { id: string; fields: ReturnType<typeof prepareDose> & { createdAt: Date } }>());
  const compoundIntents = useRef(new Map<string, { id: string; seedId: string; createdAt: Date }>());
  const takeNowTimes = useRef(new Map<string, Date>());
  const pendingWrites = useRef(0);
  const [, refreshWrites] = useState(0);
  const acceptedData = useRef({ userId: user?.id, value: mappedData });
  // Keep editors/last-compound routes mounted while a committed write is
  // being read back from the server.
  if (pendingWrites.current === 0 || acceptedData.current.userId !== user?.id) {
    acceptedData.current = { userId: user?.id, value: mappedData };
  }
  const visibleData = pendingWrites.current > 0 ? acceptedData.current.value : mappedData;
  const userCompounds = visibleData.compounds;

  const transact = async (changes: Parameters<typeof transactServer>[0], existsExactly?: () => Promise<boolean>): Promise<void> => {
    pendingWrites.current += 1;
    refreshWrites((value) => value + 1);
    try {
      if (existsExactly) await transactCreateServer(changes, existsExactly);
      else await transactServer(changes);
      await refresh();
    } finally {
      pendingWrites.current -= 1;
      refreshWrites((value) => value + 1);
    }
  };

  const [activeId, setActiveId] = useState<string>('');

  // Default the active compound to the first available once data loads.
  useEffect(() => {
    if (userCompounds.length === 0) {
      if (activeId !== '') setActiveId('');
      return;
    }
    if (!userCompounds.find((u) => u.id === activeId)) {
      setActiveId(userCompounds[0]!.id);
    }
  }, [userCompounds, activeId]);

  const active = useMemo<UserCompound | null>(
    () => userCompounds.find((u) => u.id === activeId) ?? userCompounds[0] ?? null,
    [userCompounds, activeId],
  );

  const view: ActiveView | null = useMemo(
    () =>
      active
        ? {
            uc: active,
            confirmedDoses: active.doses.filter((d) => d.status === 'confirmed'),
            pendingDoses: active.doses.filter((d) => d.status === 'pending'),
            skippedDoses: active.doses.filter((d) => d.status === 'skipped'),
          }
        : null,
    [active],
  );

  // ─── Mutations ─────────────────────────────────────────────────────────────

  const requireActive = () => {
    if (!user) throw new Error('Must be signed in');
    if (error || isStale || visibleData.error) throw new Error('Reconnect and refresh saved data before making changes. Your draft has not been saved.');
    if (!active) throw new Error('Select a compound first');
    return active;
  };

  const requireDose = (doseId: string) => {
    requireActive();
    const dose = userCompounds.flatMap((compound) => compound.doses).find((entry) => entry.id === doseId);
    if (!dose) throw new Error('Dose no longer exists. Reload and try again.');
    return dose;
  };

  const updateDose: Ctx['updateDose'] = async (doseId, patch) => {
    const dose = requireDose(doseId);
    const amount = patch.amount ?? dose.amount;
    const takenAt = patch.takenAt ?? dose.takenAt;
    const next = prepareDose({ amount, takenAt, status: patch.status ?? dose.status });
    await transact([tx.doses[doseId]!.update(next, { upsert: false })]);
  };

  const setStatus: Ctx['setStatus'] = (doseId, status) => updateDose(doseId, { status });

  const deleteDose: Ctx['deleteDose'] = async (doseId) => {
    requireDose(doseId);
    await transact([tx.doses[doseId]!.delete()]);
  };

  const createDose: Ctx['createDose'] = async ({ amount, takenAt, status }) => {
    const target = requireActive();
    const next = prepareDose({ amount, takenAt, status });
    const key = JSON.stringify([user!.id, target.id, amount, takenAt.toISOString(), status]);
    let intent = doseIntents.current.get(key);
    if (!intent) {
      intent = { id: makeId(), fields: { ...next, createdAt: new Date() } };
      doseIntents.current.set(key, intent);
    }
    const attempt = intent;
    await transact([
      tx.doses[attempt.id]!.create(attempt.fields).link({ userCompound: target.id }),
    ], async () => {
      const result = await queryServer({ doses: { $: { where: { id: attempt.id } }, userCompound: { owner: {} } } });
      const saved = result.doses[0];
      return !!saved && matchesSavedFields(saved, attempt.fields)
        && saved.userCompound?.id === target.id && saved.userCompound.owner?.id === user!.id;
    });
    doseIntents.current.delete(key);
  };

  const confirmNew: Ctx['confirmNew'] = (amount, takenAt) => createDose({ amount, takenAt, status: 'confirmed' });
  const takeNow: Ctx['takeNow'] = async (amountMg) => {
    const target = requireActive();
    const key = JSON.stringify([user!.id, target.id, amountMg]);
    const takenAt = takeNowTimes.current.get(key) ?? new Date();
    takeNowTimes.current.set(key, takenAt);
    await confirmNew(amountMg, takenAt);
    takeNowTimes.current.delete(key);
  };

  const updateCompoundSettings: Ctx['updateCompoundSettings'] = async (patch, concentrationMgPerMl) => {
    const target = requireActive();
    positive(concentrationMgPerMl, 'Concentration');
    const next = reviseSchedule(target.schedule, patch, new Date());
    const scheduleChanged = next !== target.schedule;
    if (!scheduleChanged && concentrationMgPerMl === target.concentrationMgPerMl) return;
    await transact([tx.userCompounds[target.id]!.update({
      concentrationMgPerMl,
      ...(scheduleChanged ? {
        defaultDoseMg: next.defaultDoseMg,
        cadenceHours: next.cadenceHours,
        scheduleStartsAt: next.startsAt,
        currentScheduleEffectiveAt: (next.effectiveAt ?? next.startsAt).toISOString(),
        scheduleHistory: (next.history ?? []).map(serializeVersion),
      } : {}),
    }, { upsert: false })]);
  };

  const updateSchedule: Ctx['updateSchedule'] = async (patch) => updateCompoundSettings(patch, requireActive().concentrationMgPerMl);
  const updateConcentration: Ctx['updateConcentration'] = (concentration) => updateCompoundSettings({}, concentration);

  const createCompound: Ctx['createCompound'] = async (input) => {
    if (!user) throw new Error('Must be signed in');
    if (error || isStale || visibleData.error || isLoading) throw new Error('Wait for saved data to load before adding a compound.');
    const preset = findPreset(input.compoundId);
    if (!preset) throw new Error(`Unknown compound: ${input.compoundId}`);
    positive(input.concentrationMgPerMl, 'Concentration');
    positive(input.defaultDoseMg, 'Default dose');
    validCadence(input.cadenceHours);
    validDate(input.scheduleStartsAt, 'Schedule start');
    const key = JSON.stringify([user.id, input]);
    let intent = compoundIntents.current.get(key);
    if (!intent) {
      intent = { id: makeId(), seedId: makeId(), createdAt: new Date() };
      compoundIntents.current.set(key, intent);
    }
    const { id: newId, seedId, createdAt: now } = intent;
    const fields = {
        compoundId: preset.id,
        shortCode: preset.shortCode,
        concentrationMgPerMl: input.concentrationMgPerMl,
        defaultDoseMg: input.defaultDoseMg,
        cadenceHours: input.cadenceHours,
        scheduleStartsAt: input.scheduleStartsAt,
        currentScheduleEffectiveAt: input.scheduleStartsAt.toISOString(),
        scheduleHistory: [],
        isActive: true,
        createdAt: now,
    };
    const seedFields = {
      ...prepareDose({ amount: input.defaultDoseMg, takenAt: input.scheduleStartsAt, status: 'confirmed' }, now),
      createdAt: now,
    };
    const txs = [
      tx.userCompounds[newId]!.create(fields).link({ owner: user.id }),
    ];
    if (input.seedFirstDose) {
      txs.push(tx.doses[seedId]!.create(seedFields).link({ userCompound: newId }));
    }
    await transact(txs, async () => {
      const result = await queryServer({ userCompounds: { $: { where: { id: newId } }, owner: {}, doses: {} } });
      const saved = result.userCompounds[0];
      const seed = saved?.doses?.find((dose) => dose.id === seedId);
      return !!saved && saved.owner?.id === user.id && matchesSavedFields(saved, fields)
        && (!input.seedFirstDose || (!!seed && matchesSavedFields(seed, seedFields)));
    });
    compoundIntents.current.delete(key);
    setActiveId(newId);
    return newId;
  };

  const deleteCompound: Ctx['deleteCompound'] = async (compoundId) => {
    requireActive();
    const target = userCompounds.find((uc) => uc.id === compoundId);
    if (!target) throw new Error('Compound no longer exists. Reload and try again.');
    await transact([
      ...target.doses.map((dose) => tx.doses[dose.id]!.delete()),
      tx.userCompounds[compoundId]!.delete(),
    ]);
    if (activeId === compoundId) setActiveId(userCompounds.find((uc) => uc.id !== compoundId)?.id ?? '');
  };

  const logout = () => {
    setActiveId('');
  };

  const value: Ctx = {
    userCompounds,
    loading: isLoading,
    activeId,
    setActiveId,
    active,
    view,
    setStatus,
    deleteDose,
    updateDose,
    takeNow,
    confirmNew,
    createDose,
    updateSchedule,
    updateCompoundSettings,
    updateConcentration,
    createCompound,
    deleteCompound,
    logout,
  };

  const readError = visibleData.error ?? (!data ? error?.message : undefined);
  if (readError) {
    return (
      <View className="flex-1 items-center justify-center bg-bg p-6">
        <Text className="text-lg font-semibold text-text">Unable to load your dose history</Text>
        <Text role="alert" className="mt-3 max-w-lg text-center text-sm text-textDim">{readError}</Text>
        <Text className="mt-2 max-w-lg text-center text-sm text-textMuted">Current saved records could not be verified. Reload before using projections. If a save lost confirmation, check the log before submitting it again.</Text>
        <Pressable onPress={() => window.location.reload()} className="mt-5 rounded-xl bg-accent px-5 py-3">
          <Text className="text-sm font-medium text-white">Reload</Text>
        </Pressable>
      </View>
    );
  }

  return <CompoundsContext.Provider value={value}>
    {(isStale || error) && data && <View role="status" className="border-b border-warning/30 bg-warning/10 px-4 py-2">
      <Text className="text-xs text-warning">Showing saved history{fetchedAt ? ` from ${new Date(fetchedAt).toLocaleString()}` : ''}. This view may be out of date. Reconnect before saving; unsaved changes are not included in these projections.</Text>
      <Pressable onPress={() => { void refresh().catch(() => {}); }} className="mt-1 self-start py-1">
        <Text className="text-xs font-medium text-accent">Refresh saved history</Text>
      </Pressable>
    </View>}
    {children}
  </CompoundsContext.Provider>;
}

export function useCompounds(): Ctx {
  const ctx = useContext(CompoundsContext);
  if (!ctx) throw new Error('useCompounds must be used inside CompoundsProvider');
  return ctx;
}
